import type {
  LeadBusinessSource,
  LeadCostAgreementState,
  LeadEffectiveStage,
  LeadMeetingPreference,
  LeadNotProceedingReason,
  LeadOffice,
  LeadOutstandingItemType,
  LeadSection,
  LeadStage,
  LeadTriState,
  LprRole,
  UpdateLeadContactInput,
  UpdateLeadInput
} from '@/lib/services/leads/types';
import { LEGAL_PERSONAL_REPRESENTATIVE_ROLES } from '@/lib/constants';

/**
 * Human copy for the leads board's enum values — **one definition** (LF-5985 /
 * LEADS-18).
 *
 * Copy is the frontend's, so it lives here rather than on the wire; what must
 * never be re-derived client-side is *state* (section, stall, effective stage,
 * cost-agreement standing), all of which arrives settled from `GET /leads`.
 *
 * **Published to LEADS-19 (LF-5986) and LEADS-20 (LF-5987)** — the row's grey
 * secondary note and the capture modal's source picker read these maps rather
 * than restating the strings. LEADS-19 added the outstanding-item,
 * not-proceeding and cost-agreement maps below, here rather than in the row
 * file so the drawer (LEADS-21) inherits one definition of each.
 */

/** The three sections of the In-progress tab (`functional-spec.md` §3.3). */
export const LEAD_SECTION_LABELS: Record<LeadSection, string> = {
  new_enquiry: 'New enquiry',
  pending_allocation: 'Pending allocation',
  meeting_booked: 'Meeting booked',
  pending_engagement: 'Pending engagement'
};

/**
 * Where the enquiry came from — the *channel* (§2.7). Deliberately distinct
 * from `referredBy`, which is the *person*.
 */
export const LEAD_SOURCE_LABELS: Record<LeadBusinessSource, string> = {
  phone: 'Phone',
  web_form: 'Web form',
  accountant: 'Accountant',
  financial_adviser: 'Financial adviser',
  existing_client: 'Existing client',
  wills_register: 'Wills register',
  funeral_director: 'Funeral director',
  word_of_mouth: 'Word of mouth'
};

/**
 * Every source in the order the filter menu offers them. Derived from the label
 * map so a source added to the union cannot be silently missing from the menu —
 * `Record<LeadBusinessSource, string>` fails to compile without its entry.
 */
export const LEAD_SOURCE_ORDER = Object.keys(
  LEAD_SOURCE_LABELS
) as LeadBusinessSource[];

/**
 * §3.5.5 — the row says `Awaiting cert`, not `Death certificate`.
 *
 * **The verb is the signal.** Naming the artefact alone reads equally as "we
 * received it"; only `Awaiting` says it is still outstanding.
 */
export const LEAD_OUTSTANDING_SHORT_LABELS: Record<
  LeadOutstandingItemType,
  string
> = {
  death_certificate: 'cert',
  will: 'will',
  other: 'something'
};

/** The full artefact name, for the tooltip where there is room to read it. */
export const LEAD_OUTSTANDING_LABELS: Record<LeadOutstandingItemType, string> =
  {
    death_certificate: 'death certificate',
    will: 'original will',
    other: 'outstanding item'
  };

/** §2.6 — the seven fixed reasons, as a closed row renders them. */
export const LEAD_NOT_PROCEEDING_REASON_LABELS: Record<
  LeadNotProceedingReason,
  string
> = {
  went_elsewhere: 'Went to another firm',
  cost: 'Cost',
  no_response: 'No response',
  not_proceeding_yet: 'Not proceeding yet',
  no_estate_to_administer: 'No estate to administer',
  conflict: 'Conflict',
  out_of_scope: 'Outside our scope'
};

/**
 * §3.5.6 — where the cost agreement stands.
 *
 * `none` is `Pending cost agreement` rather than "no cost agreement": a met lead
 * with nothing drafted is the rot point, so the label names the action owed
 * rather than the absence.
 */
export const LEAD_COST_AGREEMENT_LABELS: Record<
  LeadCostAgreementState,
  string
> = {
  none: 'Pending cost agreement',
  awaiting_signature: 'Awaiting signature',
  awaiting_fee: 'Awaiting fee',
  paid: 'Ready to create matter'
};

/**
 * §2.1 — how each stage reads, including the one derived rung.
 *
 * The drawer's header pill is the first surface to need these: the board shows
 * *sections*, which are a coarser grouping than a stage, so nothing before
 * LEADS-21 had to name one.
 *
 * **`Initial fee paid`, not `Retainer paid`** — the won band already reads
 * `Retained` (see `LEAD_WON_BAND_LABEL`), so "retainer" would carry two meanings
 * on one screen: the client engaging the firm, and the advance money landing in
 * trust. Only the label changes; the stage key is untouched.
 */
export const LEAD_STAGE_LABELS: Record<LeadEffectiveStage, string> = {
  new_enquiry: 'New enquiry',
  form_sent: 'Intake form sent',
  ready_to_book: 'Ready to book',
  booked: 'Booked',
  meeting_held: 'Meeting held',
  cost_agreement_sent: 'Cost agreement sent',
  cost_agreement_signed: 'Cost agreement signed',
  initial_fee_paid: 'Initial fee paid'
};

/**
 * The seven **stored** stages in §2.1's board order — what the manual override
 * (§5.6) offers.
 *
 * **`ready_to_book` is absent, and its absence is the contract.** That rung is
 * derived from a 100%-complete intake form on a `form_sent` enquiry and is never
 * stored, so offering it would build a request the backend rejects with a 400.
 * `LeadStage` rather than `LeadEffectiveStage` is what makes that a type error
 * rather than a convention — it is also why this cannot simply be
 * `Object.keys(LEAD_STAGE_LABELS)`, which would include the derived rung.
 *
 * Ordered explicitly rather than derived from anything, mirroring the backend's
 * own `STAGE_BOARD_ORDER` for the same reason it is explicit there: relying on
 * declaration order would let a cosmetic reorder silently redefine the sequence a
 * reader scans. The order is only presentational here — the override moves in
 * either direction, so nothing about it grades "forward".
 */
export const LEAD_STORED_STAGE_ORDER: readonly LeadStage[] = [
  'new_enquiry',
  'form_sent',
  'booked',
  'meeting_held',
  'cost_agreement_sent',
  'cost_agreement_signed',
  'initial_fee_paid'
];

/**
 * §2.3 — what the **won** band is called, in one place.
 *
 * The board tab and the drawer's status pill both read this. That is the whole
 * reason it is a constant: a latent defect in the prototype had the tab hardcoded
 * `Converted` while the pill said something else, so one state carried two names
 * on one screen.
 *
 * The `firm-statuses` status model would render this as `Proceeding` instead, but
 * that setting is not on the wire yet — no lead DTO carries it — so the default
 * `fixed-outcomes` wording is the only reachable value today. When the setting is
 * exposed, this constant is the single call site to widen.
 */
export const LEAD_WON_BAND_LABEL = 'Retained';

/** §2.6 — how a closed-as-lost lead's band reads. */
export const LEAD_LOST_BAND_LABEL = 'Not proceeding';

/**
 * A yes/no fact that is genuinely unknown until somebody asks (§4.5).
 *
 * **`unknown` is `Not asked yet`, not `Unknown`.** The column is a tri-state rather
 * than a nullable boolean precisely because "nobody has asked" is operationally
 * different from "asked, and the answer is no" — and only the longer label carries
 * that difference to the person reading the card.
 */
export const LEAD_TRISTATE_LABELS: Record<LeadTriState, string> = {
  yes: 'Yes',
  no: 'No',
  unknown: 'Not asked yet'
};

/**
 * The conflict check's own reading of the same tri-state (LF-6099, decision D).
 *
 * **Not `LEAD_TRISTATE_LABELS`, despite being the identical column type.** Those
 * labels describe a question put to the *client* — `Not asked yet` is literally
 * about asking somebody. The conflict check is the **firm's own task**, which is
 * exactly why it is a marked field rather than an outstanding item, so the three
 * values read as states of that task: it has been run, it has not, or nobody has
 * recorded either way.
 *
 * `no` is `Not done` rather than "No" because the value is only ever set
 * deliberately — someone looked and recorded that the check is still owed, which
 * is operationally different from `unknown`'s silence.
 */
export const LEAD_CONFLICT_CHECK_LABELS: Record<LeadTriState, string> = {
  yes: 'Done',
  no: 'Not done',
  unknown: 'Not recorded'
};

/**
 * §5.2 — how the meeting is held.
 *
 * **`video`, not the platform.** The preference is vendor-neutral because the
 * *link* is the platform (RX.8) — a firm on Teams this month and Meet the next
 * would otherwise need a schema change. `Video call` rather than "Remote" for
 * the same reason the confirmation email states the format in words: the client
 * needs to know whether to travel.
 */
export const LEAD_MEETING_PREFERENCE_LABELS: Record<
  LeadMeetingPreference,
  string
> = {
  in_person: 'In person',
  video: 'Video call'
};

/** `Geelong Office` stays as it is; `Geelong` reads as `Geelong office`. */
export function leadMeetingOfficeLabel(name: string): string {
  const trimmed = name.trim();
  return /\boffice$/i.test(trimmed) ? trimmed : `${trimmed} office`;
}

/**
 * Where a meeting is held, in the booking dialog's own words (CAL-6, CAL-11):
 * each office is its own in-person choice ("In person at Melbourne office"), an
 * in-person meeting with no office recorded reads "In person", and a video
 * meeting "Video call". The dialog's Where picker and the Meeting card both
 * read it from here, so the two cannot word the same meeting differently.
 * `null` is a meeting whose format was never recorded.
 */
export function leadMeetingWhereLabel(
  meeting: {
    preference: LeadMeetingPreference | null;
    officeId: number | null;
  },
  offices: readonly LeadOffice[]
): string | null {
  if (meeting.preference === 'video')
    return LEAD_MEETING_PREFERENCE_LABELS.video;
  if (meeting.preference !== 'in_person') return null;
  const office = offices.find((candidate) => candidate.id === meeting.officeId);
  return office
    ? `${LEAD_MEETING_PREFERENCE_LABELS.in_person} at ${leadMeetingOfficeLabel(office.name)}`
    : LEAD_MEETING_PREFERENCE_LABELS.in_person;
}

/**
 * An LPR's role as the enquiry names it (VALE-2061), off the one role
 * vocabulary. `null` is a role nobody has settled yet, which is the common
 * state before a grant: with a Will it is usually an executor, without one an
 * administrator, and only the firm can say which.
 */
export function leadLprRoleLabel(role: LprRole | null): string {
  return (
    LEGAL_PERSONAL_REPRESENTATIVE_ROLES.find((entry) => entry.value === role)
      ?.shortLabel ?? 'Role not yet known'
  );
}

/**
 * The enquirer's LPR standing and relationship, as the enquiry's own fields
 * hold them. Un-marking them an LPR clears the role, as on any other row.
 */
export function enquirerFactsPatch(
  facts: UpdateLeadContactInput
): UpdateLeadInput {
  return {
    ...(facts.isLegalPersonalRepresentative !== undefined
      ? {
          clientIsLpr: facts.isLegalPersonalRepresentative ? 'yes' : 'no',
          ...(facts.isLegalPersonalRepresentative
            ? {}
            : { clientLprRole: null })
        }
      : {}),
    ...(facts.legalPersonalRepresentativeRole !== undefined
      ? { clientLprRole: facts.legalPersonalRepresentativeRole }
      : {}),
    ...(facts.relationshipToDeceased !== undefined
      ? { relationshipToDeceased: facts.relationshipToDeceased }
      : {})
  };
}

/**
 * How an enquiry names its second person (VALE-2061). On estate
 * administration they are simply another person who contacted the firm: who
 * acts for the estate is the LPRs card's question, and "partner / spouse" read
 * as a role in the estate. On estate planning they are the partner making wills
 * alongside the client, which is what the fields were first built for.
 */
export function secondEnquirerCopy(enquiryType: string): {
  add: string;
  heading: string;
} {
  return enquiryType === 'estate-administration'
    ? { add: '+ Add another enquirer', heading: 'Second enquirer' }
    : { add: '+ Add partner/spouse details', heading: 'Partner / spouse' };
}
